import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext, API } from '@/App';
import AdminLayout from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { 
  Search, RefreshCw, Loader2, User, Car, Calendar,
  Pause, XCircle, RotateCcw, CreditCard, Filter, Eye,
  CheckCircle, Clock, AlertCircle, Ban
} from 'lucide-react';

const STATUS_CONFIG = {
  active: { label: 'Ativa', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  pending: { label: 'Pendente', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  paused: { label: 'Pausada', color: 'bg-blue-100 text-blue-700', icon: Pause },
  cancelled: { label: 'Cancelada', color: 'bg-red-100 text-red-700', icon: Ban },
  overdue: { label: 'Inadimplente', color: 'bg-orange-100 text-orange-700', icon: AlertCircle }
};

export default function AdminSubscriptions() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [subscriptions, setSubscriptions] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [filters, setFilters] = useState({ status: '', search: '' });
  const [selectedSub, setSelectedSub] = useState(null);

  useEffect(() => {
    if (!user || user.user_type !== 'admin') {
      navigate('/control-portal-clube/login');
      return;
    }
    fetchSubscriptions();
  }, [user, navigate, filters.status]);

  const fetchSubscriptions = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      
      const res = await axios.get(`${API}/admin/subscriptions?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptions(res.data.subscriptions || []);
      setStats(res.data.stats || {});
    } catch (error) {
      console.error(error);
      toast.error('Erro ao carregar assinaturas');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (subId, action, reason = '') => {
    setActionLoading(subId);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/subscriptions/${subId}/${action}`, 
        { reason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Assinatura ${action === 'cancel' ? 'cancelada' : action === 'reactivate' ? 'reativada' : 'pausada'} com sucesso!`);
      fetchSubscriptions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao executar ação');
    } finally {
      setActionLoading(null);
    }
  };

  const viewDetails = async (subId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/subscriptions/${subId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedSub(res.data.subscription);
    } catch (error) {
      toast.error('Erro ao carregar detalhes');
    }
  };

  const filteredSubs = subscriptions.filter(sub => {
    if (!filters.search) return true;
    const search = filters.search.toLowerCase();
    return (
      sub.client?.name?.toLowerCase().includes(search) ||
      sub.client?.email?.toLowerCase().includes(search) ||
      sub.id?.toLowerCase().includes(search)
    );
  });

  if (loading) {
    return (
      <AdminLayout title="Gestão de Assinaturas" subtitle="Carregando...">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Gestão de Assinaturas</h1>
            <p className="text-slate-500">{subscriptions.length} assinaturas</p>
          </div>
          <Button variant="outline" size="sm" onClick={fetchSubscriptions}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Object.entries(STATUS_CONFIG).map(([key, config]) => {
            const Icon = config.icon;
            return (
              <Card 
                key={key} 
                className={`p-4 cursor-pointer transition-all ${filters.status === key ? 'ring-2 ring-primary' : ''}`}
                onClick={() => setFilters({ ...filters, status: filters.status === key ? '' : key })}
              >
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${config.color}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats[key] || 0}</p>
                    <p className="text-xs text-muted-foreground">{config.label}</p>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Filters */}
        <div className="flex gap-4 items-center">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input 
              placeholder="Buscar por cliente, e-mail ou ID..."
              className="pl-10"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            />
          </div>
          {filters.status && (
            <Button variant="outline" size="sm" onClick={() => setFilters({ ...filters, status: '' })}>
              <Filter className="w-4 h-4 mr-2" /> Limpar filtro
            </Button>
          )}
        </div>

        {/* Detail Modal */}
        {selectedSub && (
          <Card className="p-6 mb-4">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg font-semibold">Detalhes da Assinatura</h3>
              <Button variant="ghost" size="sm" onClick={() => setSelectedSub(null)}>
                <XCircle className="w-4 h-4" />
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <User className="w-4 h-4" /> Cliente
                </h4>
                <p className="text-sm">{selectedSub.client?.name || 'N/A'}</p>
                <p className="text-sm text-muted-foreground">{selectedSub.client?.email}</p>
                <p className="text-sm text-muted-foreground">{selectedSub.client?.cpf_cnpj}</p>
              </div>
              <div>
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <Car className="w-4 h-4" /> Veículo
                </h4>
                <p className="text-sm">{selectedSub.vehicle?.brand} {selectedSub.vehicle?.model}</p>
                <p className="text-sm text-muted-foreground">{selectedSub.vehicle?.plate}</p>
              </div>
              <div>
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <CreditCard className="w-4 h-4" /> Plano
                </h4>
                <p className="text-sm font-medium">{selectedSub.plan}</p>
                <p className="text-sm text-muted-foreground">
                  Criado em: {selectedSub.created_at ? new Date(selectedSub.created_at).toLocaleDateString('pt-BR') : 'N/A'}
                </p>
              </div>
              <div>
                <h4 className="font-medium mb-2">Histórico de Pagamentos</h4>
                {selectedSub.payment_history?.length > 0 ? (
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {selectedSub.payment_history.slice(0, 5).map((p, i) => (
                      <div key={i} className="text-sm flex justify-between">
                        <span>{new Date(p.created_at).toLocaleDateString('pt-BR')}</span>
                        <span className={p.status === 'paid' ? 'text-green-600' : 'text-yellow-600'}>
                          R$ {p.amount?.toFixed(2)} - {p.status}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">Nenhum pagamento registrado</p>
                )}
              </div>
            </div>
          </Card>
        )}

        {/* Subscriptions List */}
        <div className="space-y-3">
          {filteredSubs.map(sub => {
            const statusConfig = STATUS_CONFIG[sub.status] || STATUS_CONFIG.pending;
            const StatusIcon = statusConfig.icon;
            
            return (
              <Card key={sub.id} className="p-4">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className={`p-2 rounded-lg ${statusConfig.color}`}>
                      <StatusIcon className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{sub.client?.name || 'Cliente não identificado'}</span>
                        <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">{sub.client?.email}</p>
                      <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Car className="w-3 h-3" />
                          {sub.vehicle?.brand} {sub.vehicle?.model}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {sub.created_at ? new Date(sub.created_at).toLocaleDateString('pt-BR') : 'N/A'}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-2">
                    <Button variant="outline" size="sm" onClick={() => viewDetails(sub.id)}>
                      <Eye className="w-4 h-4 mr-1" /> Ver
                    </Button>
                    
                    {sub.status === 'active' && (
                      <>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleAction(sub.id, 'pause', 'Pausada pelo admin')}
                          disabled={actionLoading === sub.id}
                        >
                          <Pause className="w-4 h-4 mr-1" /> Pausar
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="text-red-600"
                          onClick={() => handleAction(sub.id, 'cancel', 'Cancelada pelo admin')}
                          disabled={actionLoading === sub.id}
                        >
                          <XCircle className="w-4 h-4 mr-1" /> Cancelar
                        </Button>
                      </>
                    )}
                    
                    {(sub.status === 'cancelled' || sub.status === 'paused') && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="text-green-600"
                        onClick={() => handleAction(sub.id, 'reactivate', 'Reativada pelo admin')}
                        disabled={actionLoading === sub.id}
                      >
                        <RotateCcw className="w-4 h-4 mr-1" /> Reativar
                      </Button>
                    )}
                    
                    {actionLoading === sub.id && (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {filteredSubs.length === 0 && (
          <Card className="p-8 text-center">
            <CreditCard className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Nenhuma assinatura encontrada</h3>
            <p className="text-muted-foreground">
              {filters.status || filters.search 
                ? 'Tente ajustar os filtros de busca'
                : 'As assinaturas dos clientes aparecerão aqui'}
            </p>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
}
